"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  COOKIE_CONSENT_POLICY_VERSION,
  REQUIRED_CATEGORY_IDS,
  type CookieCategoryId,
} from "@/app/lib/cookie-consent";

type ConsentContextType = {
  categoriesAccepted: CookieCategoryId[] | null;
  isAccepted: (category: CookieCategoryId) => boolean;
};

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
};

type ConsentProviderProps = {
  children: React.ReactNode;
  initialConsent: { categoriesAccepted: CookieCategoryId[] | null; policyVersion: string | null };
  gpcHeaderDetected: boolean;
};

const hasCurrentRecord = (
  initialConsent: ConsentProviderProps["initialConsent"],
): boolean =>
  initialConsent.categoriesAccepted !== null &&
  initialConsent.policyVersion === COOKIE_CONSENT_POLICY_VERSION;

export const ConsentProvider = ({
  children,
  initialConsent,
  gpcHeaderDetected,
}: ConsentProviderProps) => {
  const [categoriesAccepted, setCategoriesAccepted] = useState<CookieCategoryId[] | null>(
    hasCurrentRecord(initialConsent) ? initialConsent.categoriesAccepted : null,
  );
  const gpcDetectedRef = useRef(gpcHeaderDetected);
  const initializedRef = useRef(false);

  // Only remaining caller is the GPC auto-opt-out path below — no UI in this
  // app writes a consent record anymore, that's handled by Google's AdSense
  // consent banner now.
  const submitConsent = useCallback(async (categories: CookieCategoryId[]) => {
    const merged = Array.from(new Set([...REQUIRED_CATEGORY_IDS, ...categories]));

    const response = await fetch("/api/consent", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        categoriesAccepted: merged,
        gpcDetected: gpcDetectedRef.current,
        captureMethod: "gpc-auto",
      }),
    });

    if (!response.ok) return;

    const data = (await response.json()) as { categoriesAccepted?: CookieCategoryId[] };
    if (data.categoriesAccepted) {
      setCategoriesAccepted(data.categoriesAccepted);
    }
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (hasCurrentRecord(initialConsent)) return;

    const gpcDetected =
      gpcHeaderDetected ||
      (typeof navigator !== "undefined" &&
        (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true);
    gpcDetectedRef.current = gpcDetected;

    // No GPC signal: nothing to auto-record here. Non-essential consent for
    // this case is Google's AdSense consent banner's responsibility now.
    if (!gpcDetected) return;

    void submitConsent(REQUIRED_CATEGORY_IDS);
    // Only ever needs to run once on mount — initial consent/GPC state is a
    // first-paint snapshot, not something that should re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAccepted = useCallback(
    (category: CookieCategoryId) =>
      REQUIRED_CATEGORY_IDS.includes(category) || (categoriesAccepted?.includes(category) ?? false),
    [categoriesAccepted],
  );

  return (
    <ConsentContext.Provider value={{ categoriesAccepted, isAccepted }}>
      {children}
    </ConsentContext.Provider>
  );
};
